import React, { useEffect, useState, useContext } from 'react';
import {IBaseElementProps} from "../BasicElement";
import Loading from "../Loading";
import calculator from "../../assets/images/calculator.svg";
import clock from "../../assets/images/clock.svg";
import paint from "../../assets/images/paint.svg";
import book from "../../assets/images/book.svg";
import globe from "../../assets/images/globe.svg";
import pencilCase from "../../assets/images/pencil-case.svg";
import Button from "../Button";
import RawHTML from "../RawHTML";
import VHSFunctions from "../VHSFunctions";
import {createFocusTrap} from 'focus-trap';

export interface IVHSRegistrationModalProps extends IBaseElementProps {
	formLoaded?: boolean;
	studentRegistrationForm?: any;
	setCreatingStudent?: (value: boolean) => void;
	setFormLoaded?: (value: boolean) => void;
	creatingStudent?: boolean;
	pluginURI?: string;
	stylesheetURI?: string;
}

/**
 * FAQs
 * @block
 * @icon download
 */
const VHSRegistrationModal: React.FC<IVHSRegistrationModalProps> = ({
	formLoaded,
	studentRegistrationForm,
	setCreatingStudent,
	setFormLoaded,
	creatingStudent,
	pluginURI,
	stylesheetURI,
}) => {
	useEffect(() => {
		// @ts-ignore
		const acf = window?.acf;
		if (creatingStudent && acf) {
			// The following is to address the keyboard accessibility issues with the datepicker
			const datePickerInit = ($input) => {
				// The modal has focus trapped.  We're going to create another focus trap for the datepicker.
				const dp = $input.data('datepicker').dpDiv.closest('.acf-ui-datepicker');
				const trap = createFocusTrap(dp[0],{escapeDeactivates:false}); // we'll handle the escape key ourselves
				dp.on('keyup',function(e) {
					// Catch the escape key and close the datepicker
					if ( 27 === e.which ) {
						e.stopPropagation();
						$input.datepicker('hide');
					}
				});
				// This function looks for the most appropriate date to focus on - either the
				// selected ( active ) date, the highlighted ( today ) date, or day 1.
				const setFocus = () => {
					// The timeout is so the datepicker has time to render before we set focus.
					setTimeout( () => {
						const active = dp.find('a.ui-state-active');
						const highlighted = dp.find('a.ui-state-highlight');
						const el = active.length
							? active
							: ( highlighted.length
								? highlighted
								: dp.find('a.ui-state-default').first()
							)
						;
						trap.activate();
						el.focus();
					});
				}
				$input.datepicker('option','beforeShow',setFocus);
				$input.datepicker('option','onChangeMonthYear',setFocus);
				$input.datepicker('option','onClose',() => {
					trap.deactivate();
					$input.focus();
				});
				// This catches the enter key and opens the datepicker as opposed to submitting the form.
				$input.on('keydown',function(e){
					if ( 13 === e.which ) {
						e.preventDefault();
						$input.datepicker('show');
					}
				});
			}
			acf.addAction('date_picker_init',datePickerInit);
			acf.doAction('prepare');
			// remove the city selector script and refresh it
			let script = document.querySelector(
				'script[src*="/acf-city-selector/assets/js/city-selector.js"]',
			);
			script.parentNode.removeChild(script);
			script = document.createElement('script');
			// @ts-ignore
			script.src = `${pluginURI}/acf-city-selector/assets/js/city-selector.js`;
			document.head.appendChild(script);
			script = document.createElement('script');
			// @ts-ignore
			script.innerHTML =
				'tbk_acf_functions = {}; tbk_acf_functions_show_register_button = false;';
			document.head.appendChild(script);
			// @ts-ignore
			script = document.createElement('link');
			// @ts-ignore
			script.rel = 'stylesheet';
			// @ts-ignore
			script.href = `${stylesheetURI}/assets/css/tbk-acf-functions.css`;
			document.head.appendChild(script);
			script = document.createElement('script');
			// @ts-ignore
			script.src = `${stylesheetURI}/assets/js/tbk-common.js`;
			document.head.appendChild(script);
			script = document.createElement('script');
			// @ts-ignore
			script.src = `${stylesheetURI}/assets/js/tbk-registration-functions.js`;
			document.head.appendChild(script);
			script = document.createElement('script');
			// @ts-ignore
			script.src = `${stylesheetURI}/assets/js/acf-region-mod.js`;
			document.head.appendChild(script);
			VHSFunctions.initTooltips();

			// @ts-ignore
			const $ = window.jQuery;
			// These next two listeners deal with select2 dropdowns.  We'll trap focus in them when they open
			// and we'll handle the escape key ourselves.  The escape key is a bit tricky.  The modal listens
			// for a keyup event, but the dropdown stopsPropagation of the initial keydown event, so we
			// actually attach the keyup listener to body ( one below document, which is where the modal listens )
			// and if the target is a select2 dropdown, we prevent the default action.
			const select2Listener = function(e) {
				const dropdown = $('body > .select2-container--open');
				const target = $(e.target);
				if ( dropdown.length ) {
					const trap = createFocusTrap(dropdown[0],{escapeDeactivates:false}); // we'll handle the escape key ourselves
					trap.activate();

					target.one('select2:close',function(e){
						trap.deactivate();
					});
				}
			}
			const bodyEscapeListener = function(e) {
				if ( 27 === e.which && e.target.classList.contains('select2-selection') ) {
					e.preventDefault();
				}
			};
			$(document).on('select2:open',select2Listener);
			$('body').on('keyup',bodyEscapeListener);

			// // Finally deal with the tooltips on removing a guardian
			const tooltipListener = function(e) {
				const tooltip = $('.acf-tooltip.-confirm');
				if ( tooltip.length ) {
					const trap = createFocusTrap(tooltip[0],{escapeDeactivates:false,clickOutsideDeactivates:true}); // we'll handle the escape key ourselves
					trap.activate();
					tooltip.find('a').on('click',function(e){
						trap.deactivate();
					});
					tooltip.on('keyup',function(e){
						if ( 27 === e.which ) {
							tooltip.find('a[data-event="cancel"]').trigger('click');
							e.preventDefault();
						}
					});
				}
			};
			$(document).on('click', 'a[data-event="remove-row"]', tooltipListener);

			// Cleanup after ourselves.
			return () => {
				acf.removeAction('date_picker_init',datePickerInit);
				$(document).off('select2:open',select2Listener);
				$('body').off('keyup',bodyEscapeListener);
				$(document).off('click', 'a[data-event="remove-row"]', tooltipListener);
			};
		}
	}, [creatingStudent]);

	return (
		<>
		<div
			style={{
				display: formLoaded
					? 'none'
					: '',
			}}
		>
			<Loading />
		</div>
		<div
			className="vhs-cart__registration"
			style={{
				display: !formLoaded
					? 'none'
					: '',
			}}
		>
			<img
				className="vhs-cart__calculator"
				src={calculator}
				alt="Calculator"
			/>
			<img
				className="vhs-cart__clock"
				src={clock}
				alt="Clock"
			/>
			<img
				className="vhs-cart__paint"
				src={paint}
				alt="Paint"
			/>
			<img
				className="vhs-cart__book"
				src={book}
				alt="Book"
			/>
			<img
				className="vhs-cart__globe"
				src={globe}
				alt="Globe"
			/>
			<img
				className="vhs-cart__pencil-case"
				src={pencilCase}
				alt="Pencil Case"
			/>
			<Button
				id="vhs-cart__back"
				className="vhs-cart__back"
				onClick={() => {
					setCreatingStudent(false);
					setFormLoaded(false);
				}}
			>
				Back
			</Button>

			<RawHTML
				html={studentRegistrationForm}
			/>
		</div>
	</>);
};

export default VHSRegistrationModal;
